/**
 * 재처리 결과 채택(POST /apply) + 폐기(DELETE /pending) mutation 훅.
 *
 * 성공 시:
 *   - Job 상세 캐시 무효화 (pending_extraction 해제 반영)
 *   - diff 캐시 제거 (다음 재처리 시 새로 조회되도록)
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  applyReextraction,
  discardPending,
  type ApplyReextractionPayload,
} from "@/lib/api/records";
import { jobDetailKey } from "@/features/jobs/useJobDetail";
import { recordDiffKey } from "./useRecordDiff";
import type { JobDetailOut, RecordOut } from "@/lib/api/jobs";

interface ApplyVars {
  jobId: string;
  fileId: string;
  payload: ApplyReextractionPayload;
}

/** 반영/폐기 응답(갱신된 record)으로 job 상세 캐시를 즉시 패치.
 * invalidate 후 refetch 이전까지 남는 stale ``pending_extraction`` 때문에
 * 편집 화면 진입 시 diff 오버레이가 잠깐 다시 열리는 깜빡임을 방지한다. */
function patchRecordInJobCache(
  qc: ReturnType<typeof useQueryClient>,
  jobId: string,
  fileId: string,
  record: RecordOut,
): void {
  qc.setQueryData<JobDetailOut>(jobDetailKey(jobId), (prev) => {
    if (!prev) return prev;
    return {
      ...prev,
      files: prev.files.map((f) => (f.id === fileId ? { ...f, record } : f)),
    };
  });
}

export function useApplyReextraction() {
  const qc = useQueryClient();
  return useMutation<RecordOut, Error, ApplyVars>({
    mutationFn: ({ jobId, fileId, payload }) => applyReextraction(jobId, fileId, payload),
    onSuccess: (data, vars) => {
      patchRecordInJobCache(qc, vars.jobId, vars.fileId, data);
      qc.invalidateQueries({ queryKey: jobDetailKey(vars.jobId) });
      qc.removeQueries({ queryKey: recordDiffKey(vars.jobId, vars.fileId) });
    },
  });
}

interface DiscardVars {
  jobId: string;
  fileId: string;
}

export function useDiscardPending() {
  const qc = useQueryClient();
  return useMutation<RecordOut, Error, DiscardVars>({
    mutationFn: ({ jobId, fileId }) => discardPending(jobId, fileId),
    onSuccess: (data, vars) => {
      patchRecordInJobCache(qc, vars.jobId, vars.fileId, data);
      qc.invalidateQueries({ queryKey: jobDetailKey(vars.jobId) });
      qc.removeQueries({ queryKey: recordDiffKey(vars.jobId, vars.fileId) });
    },
  });
}
